"use client";

import { useFunnel, type FunnelStep } from "@/hooks/useFunnel";
import { VSL } from "./VSL";
import { QualificationForm } from "./QualificationForm";
import { CalModal } from "./CalModal";
import { ThankYou } from "./ThankYou";
import { LOOM_THUMBNAIL } from "@/lib/config";
import { cn } from "@/lib/utils";

const CTA_LABEL = "Accéder à la vidéo →";

// ============================================================================
// Funnel — SPA pilotée par la route. `step` vient de la page (/lp, /vsl, …).
// ============================================================================
export function Funnel({ step }: { step: FunnelStep }) {
  const f = useFunnel(step);

  // ----- /ty : confirmation ------------------------------------------------
  if (step === "ty") {
    return (
      <main className="flex min-h-dvh w-full flex-col items-center justify-center px-5 py-12">
        <ThankYou firstName={f.lead?.firstName} />
      </main>
    );
  }

  // ----- /nok : profil non qualifié ---------------------------------------
  if (step === "nok") {
    return (
      <main className="flex min-h-dvh w-full flex-col items-center justify-center px-5 py-12">
        <div className="w-full max-w-md rounded-2xl border border-ink/10 bg-white p-6 text-center">
          <p className="font-semibold text-ink">Merci d’avoir voulu réserver 🙏</p>
          <p className="mt-2 text-sm text-ink/70">
            Nous sommes complets pour le moment sur cette cohorte. On garde votre
            profil de côté et on revient vers vous dès qu’une place se libère.
          </p>
        </div>
      </main>
    );
  }

  // ----- /vsl et /vsl-free : la VSL ---------------------------------------
  if (step === "vsl" || step === "vsl-free") {
    return (
      <main className="flex min-h-dvh w-full flex-col items-center justify-center px-5 py-12">
        <div className="flex w-full max-w-3xl flex-col items-center text-center">
          <h2 className="mb-5 text-balance text-xl font-extrabold sm:text-2xl">
            {f.lead?.firstName ? `Merci ${f.lead.firstName}. ` : ""}Voici la vidéo —
            regardez-la jusqu’au bout, tout s’y explique.
          </h2>
          <VSL />

          <div className="mt-9 w-full max-w-xl text-center">
            <p className="text-base font-semibold text-ink">
              Vous vous reconnaissez dans cette vidéo ?
            </p>
            <p className="mx-auto mt-3 max-w-lg text-sm text-ink/70">
              La prochaine étape, c’est un entretien de 20 minutes pour comprendre
              votre quotidien et voir si votre profil correspond à cette première
              cohorte. 10 places. On reçoit près de 20 candidatures par jour — les
              créneaux partent vite.
            </p>
            <CTA onClick={f.openBooking} className="mt-7 w-full sm:w-auto">
              Réserver mon entretien de sélection →
            </CTA>
          </div>
        </div>

        {/* /vsl-free : formulaire de qualification AVANT la prise de RDV */}
        <FormModal open={f.formOpen} onClose={f.closeForm}>
          <QualificationForm onComplete={f.completeForm} />
        </FormModal>

        <CalModal
          open={f.calOpen}
          onClose={f.closeCal}
          onBooked={f.onBooked}
          prefill={{ name: f.lead?.nomComplet, email: f.lead?.email }}
          utms={f.utms}
        />
      </main>
    );
  }

  // ----- /lp : landing (courte, droit à l'essentiel) ----------------------
  return (
    <main className="w-full">
      {/* Bandeau / accroche (variable A/B) */}
      <div className="w-full border-b border-brand/15 bg-brand/[0.06] px-5 py-3 text-center">
        <p className="mx-auto max-w-3xl text-pretty text-xs font-medium text-brand sm:text-sm">
          {f.home.banner}
        </p>
      </div>

      {/* Hero : promesse → vidéo → copie → CTA */}
      <section className="mx-auto flex max-w-2xl flex-col items-center px-5 pt-6 pb-16 text-center sm:pt-16">
        <h1 className="text-balance text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl">
          {f.home.headline}
        </h1>
        <p className="mx-auto mt-5 max-w-xl text-pretty text-base text-ink/75 sm:text-lg">
          {f.home.subheadline}
        </p>

        {/* Vidéo (verrouillée jusqu'au formulaire) */}
        <VideoTeaser onClick={() => f.openForm("video_teaser")} className="mt-9" />

        {/* Copie sous la vidéo (variable A/B — cf. HOME_VARIANTS.body) */}
        <div className="mx-auto mt-6 max-w-xl space-y-3 text-pretty text-sm text-ink/75 sm:text-base">
          {f.home.body.map((para, i) => (
            <p key={i}>{para}</p>
          ))}
          <p className="font-medium text-ink">
            Regardez-la maintenant — 30 secondes pour personnaliser ce qu’on vous montre.
          </p>
        </div>

        <CTA onClick={() => f.openForm("hero")} className="mt-8 w-full sm:w-auto">
          {CTA_LABEL}
        </CTA>
        <p className="mt-3 text-xs text-ink/45">
          Gratuit · sans code · vos outils habituels · sélection sur entretien
        </p>
      </section>

      {/* Formulaire en modal (le fond ramène à la LP) */}
      <FormModal open={f.formOpen} onClose={f.closeForm}>
        <QualificationForm onComplete={f.completeForm} />
      </FormModal>
    </main>
  );
}

// ===== petits composants ====================================================
function CTA({
  children,
  onClick,
  className,
}: {
  children: React.ReactNode;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center justify-center rounded-xl bg-gold px-8 py-4 text-base font-semibold text-ink shadow-lg shadow-gold/25 transition hover:scale-[1.02] hover:bg-gold-dark active:scale-100",
        className
      )}
    >
      {children}
    </button>
  );
}

// Vignette vidéo verrouillée (GIF Loom) : ouvre le formulaire au clic.
function VideoTeaser({ onClick, className }: { onClick: () => void; className?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Accéder à la vidéo"
      className={cn(
        "group relative grid aspect-video w-full place-items-center overflow-hidden rounded-2xl border border-ink/10 bg-ink shadow-2xl shadow-black/40",
        className
      )}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={LOOM_THUMBNAIL}
        alt="Aperçu de la vidéo"
        className="absolute inset-0 h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
      />
      <div className="absolute inset-0 bg-black/25" />
      <span className="relative grid h-16 w-16 place-items-center rounded-full bg-gold shadow-lg transition group-hover:scale-110">
        <svg viewBox="0 0 24 24" className="h-7 w-7 fill-ink">
          <path d="M8 5v14l11-7z" />
        </svg>
      </span>
      <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 bg-black/45 py-2.5 text-xs font-medium text-cream backdrop-blur-sm">
        ▶ Cliquez pour accéder · 30 s pour personnaliser
      </span>
    </button>
  );
}

// Modal générique : overlay + clic sur le fond pour fermer.
function FormModal({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm sm:items-center"
      role="dialog"
      aria-modal="true"
    >
      <div onClick={(e) => e.stopPropagation()} className="relative my-8 w-full max-w-md">
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="absolute -top-3 -right-3 z-10 grid h-9 w-9 place-items-center rounded-full border border-ink/10 bg-white text-lg text-ink shadow-md transition hover:bg-cream-dark"
        >
          ×
        </button>
        {children}
      </div>
    </div>
  );
}
